import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThreadSummaryDto } from "@tc/shared";
import { Avatar, Icon } from "@/components/ui";
import { useClubFilter } from "@/lib/clubs/club-filter";
import { useFormatters } from "@/lib/i18n/format";
import { ChatPane, type ChatSource } from "./ChatPane";
import { messagingApi } from "./api";

type Selected =
  | { kind: "general"; clubId: string }
  | { kind: "trip"; tripId: string };

function keyOf(sel: Selected): string {
  return sel.kind === "general" ? `general:${sel.clubId}` : `trip:${sel.tripId}`;
}

/**
 * The messages inbox (TW-3454) — the club's general (non-trip) thread plus every trip thread it can
 * see, in a two-pane list/conversation layout. Mounted at `/messages` for both audiences via
 * {@link ClubMessagesPage}/{@link OpsMessagesPage}; back office scopes to one club via the
 * ClubFilter. Selecting a thread points the reusable {@link ChatPane} at its endpoints.
 */
function InboxView({ scopeId }: { scopeId?: string }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [threads, setThreads] = useState<ThreadSummaryDto[] | null>(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<Selected | null>(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      const list = await messagingApi.inbox(scopeId);
      setThreads(list);
      // Default the open conversation to the general thread (or the first thread) once loaded.
      setSelected((prev) => {
        if (prev) return prev;
        const general = list.find((th) => th.type === "GENERAL");
        if (general) return { kind: "general", clubId: general.clubId };
        const first = list[0];
        if (first) return first.tripId ? { kind: "trip", tripId: first.tripId } : { kind: "general", clubId: first.clubId };
        return null;
      });
    } catch {
      setError(true);
    }
  }, [scopeId]);

  // Reset the selection when the club scope changes (back office switching clubs).
  useEffect(() => {
    setSelected(null);
    setThreads(null);
  }, [scopeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const source = useMemo<ChatSource | null>(() => {
    if (!selected) return null;
    if (selected.kind === "trip") {
      const { tripId } = selected;
      return {
        load: () => messagingApi.getTripThread(tripId),
        post: (body) => messagingApi.postTrip(tripId, body),
        markRead: () => messagingApi.markTripRead(tripId),
      };
    }
    const { clubId } = selected;
    return {
      load: () => messagingApi.getGeneral(clubId),
      post: (body) => messagingApi.postGeneral(body, clubId),
      markRead: () => messagingApi.markGeneralRead(clubId),
    };
  }, [selected]);

  const selectedKey = selected ? keyOf(selected) : "";

  // The open conversation's identity for the panel header: trip name + reference, or "General".
  const header = useMemo(() => {
    if (!selected || !threads) return undefined;
    const th = threads.find((row) => keyOf(toSelected(row)) === selectedKey);
    if (!th) return undefined;
    return th.type === "GENERAL"
      ? { title: t("messages.generalTitle") }
      : { title: th.title, reference: th.tripReference };
  }, [selected, threads, selectedKey, t]);

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t("messages.title")}</h1>
        <div className="sub">{t("messages.inboxSub")}</div>
      </div>

      {error ? (
        <div className="empty">{t("messages.loadError")}</div>
      ) : threads === null ? (
        <div className="empty">{t("common.loading")}</div>
      ) : (
        <div className="msg-layout">
          <div className="msg-list">
            {threads.length === 0 ? (
              <div className="empty" style={{ padding: 24 }}>{t("messages.noThreads")}</div>
            ) : (
              threads.map((th) => {
                const isSel = selected ? keyOf(selected) === keyOf(toSelected(th)) : false;
                const label = th.type === "GENERAL" ? t("messages.generalTitle") : th.title;
                return (
                  <button
                    key={th.threadId}
                    className={`msg-thread-item${isSel ? " active" : ""}`}
                    onClick={() => setSelected(toSelected(th))}
                  >
                    {th.type === "GENERAL" ? (
                      <Avatar initials="" icon="star" size={36} iconSize={16} />
                    ) : (
                      <Avatar initials="TC" size={36} />
                    )}
                    <div className="mt-body">
                      <div className="mt-top">
                        <b>{label}</b>
                        <span className="mt-time">
                          {th.lastMessageAt ? fmt.relative(th.lastMessageAt) : ""}
                        </span>
                      </div>
                      <div className="mt-bottom">
                        <span className="mt-prev">{th.lastMessagePreview ?? t("messages.noPreview")}</span>
                        {th.unreadCount > 0 && <span className="mt-badge">{th.unreadCount}</span>}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="msg-panel-wrap">
            {source ? (
              <ChatPane source={source} sourceKey={selectedKey} header={header} />
            ) : (
              <div className="empty">
                <div className="empty-ic">
                  <Icon name="messages" size={22} />
                </div>
                <div className="sub">{t("messages.selectThread")}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Map a summary row to the panel selection it opens. */
function toSelected(th: ThreadSummaryDto): Selected {
  return th.type === "TRIP" && th.tripId
    ? { kind: "trip", tripId: th.tripId }
    : { kind: "general", clubId: th.clubId };
}

/** Club inbox — the club's own general + trip threads (RLS-scoped; no club switcher). */
export function ClubMessagesPage() {
  return <InboxView />;
}

/** Back-office inbox — every club's threads, scoped by the ClubFilter. */
export function OpsMessagesPage() {
  const { scopeId } = useClubFilter();
  return <InboxView scopeId={scopeId} />;
}
